// react components
import React, {
    useEffect,
    useState,
} from 'react'
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material'
import {
    useDispatch,
    useSelector,
} from 'react-redux'

// components
import {
    IconBlockSite,
} from 'components'

// data
import {
    defaultReduxState,
    reduxModalErrorEventHandlerSite,
} from 'data'

// pages
import {
    TemplateBlock797DateTimeSite,
    TemplateBlock797LocationSite,
    TemplateBlock797TextAreaSite,
} from 'pages'

// serializers
import {
    CustomCSSProperties,
} from 'serializers/site'

// types
type RecurrenceType = 'none' | 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'custom'

// props
type TemplateBlock797MultiDateFormModalSiteProps = {
    multiDateData: {
        fid: number
        description?: string
        address_json?: any
        address_additional?: string
        start_date?: string
        end_date?: string
    }[]
    setMultiDateData: React.Dispatch<React.SetStateAction<{
        fid: number
        description?: string
        address_json?: any
        address_additional?: string
        start_date?: string
        end_date?: string
    }[]>>
    defaultFields?: any
    objectId: number | string
    onCloseHelperModal: () => void
    onChange: any
    stylesNew: CustomCSSProperties | undefined
    setDateId: any
}

// main
export const TemplateBlock797MultiDateFormModalSite: React.FC<TemplateBlock797MultiDateFormModalSiteProps> = React.memo(({
    setMultiDateData,
    multiDateData,
    defaultFields,
    objectId,
    onCloseHelperModal,
    onChange,
    stylesNew,
    setDateId,
}) => {

    const dispatch = useDispatch()
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)
    const reduxText = useSelector((state: defaultReduxState) => state.reduxText.data)

    const deviceType = reduxModalSite.deviceType

    type contactErrorsType = {
        description?: string
        end_date?: string
        start_date?: string
        main_error?: string
        non_field_errors?: string
        address_json?: string
        address_additional?: string
    }

    type contactFieldsType = {
        description?: string
        end_date?: string
        start_date?: string
        address_json?: any
        address_additional?: string
    }

    const contactErrorsInitial = {
        description: '',
        end_date: '',
        start_date: '',
        main_error: '',
        non_field_errors: '',
        address_json: '',
        address_additional: '',
    }

    const contactFieldsInitial = {
        description: '',
        end_date: '',
        start_date: '',
        address_json: '',
        address_additional: '',
    }

    const [errors, setErrors] = useState<contactErrorsType>(contactErrorsInitial)
    const [fields, setFields] = useState<contactFieldsType>(contactFieldsInitial)
    const [recurrenceType, setRecurrenceType] = useState<RecurrenceType | undefined>(objectId === 'new' ? undefined : 'none')
    const [recurrenceCount, setRecurrenceCount] = useState<number>(2)
    const [recurrenceError, setRecurrenceError] = useState<string | undefined>(undefined)

    const isNewEntry = objectId === 'new'

    useEffect(() => {
        // @ts-ignore
        if (objectId && objectId !== 'new') {
            // Editing mode - load existing item
            const existingItem = multiDateData.find((item) => item.fid === objectId)
            // Parse address_json if it's a string
            const fieldsData = { ...existingItem }
            if (existingItem?.address_json && typeof existingItem.address_json === 'string') {
                try {
                    fieldsData.address_json = JSON.parse(existingItem.address_json)
                } catch (e) {
                    // If parsing fails, keep it as string
                }
            }
            setFields(fieldsData || contactFieldsInitial)
        } else if (objectId === 'new' && defaultFields) {
            // Creating mode - prefill with default fields from parent
            const newFields = { ...contactFieldsInitial }
            if (defaultFields.description) newFields.description = defaultFields.description
            if (defaultFields.address_json) newFields.address_json = defaultFields.address_json
            if (defaultFields.address_additional) newFields.address_additional = defaultFields.address_additional
            // Note: Don't prefill dates for new entries as they should be entered or calculated from recurrence
            setFields(newFields)
        }
    }, [
        objectId,
    ])

    function handleInputChange(name: string, value: any, label: string, stripe_field_type: string | undefined, stringValue?: string) {
        try {
            // setButtonDisabled(false)
            setFields({
                ...fields,
                [name]: value,
            })
            setErrors({})
            setRecurrenceError(undefined)
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'TemplateBlock797MultiDateFormModalSite',
                'handleInputChange',
            ))
        }
    }

    function generateRecurrenceDates(startDate: string, endDate: string, type: RecurrenceType, count: number) {
        const dates: { start: Date, end: Date }[] = []
        const start = new Date(startDate)
        const end = new Date(endDate)
        const duration = end.getTime() - start.getTime() // Preserve duration
        const maxDate = new Date(start)
        maxDate.setMonth(maxDate.getMonth() + 4) // 4 months max

        const currentDate = new Date(start)
        let occurrences = 0

        while (occurrences < count && currentDate <= maxDate) {
            const occurrenceEnd = new Date(currentDate.getTime() + duration)

            // Add this occurrence
            dates.push({
                start: new Date(currentDate),
                end: occurrenceEnd
            })
            occurrences++

            // Calculate next occurrence based on type
            switch (type) {
                case 'daily':
                    currentDate.setDate(currentDate.getDate() + 1)
                    break
                case 'weekdays':
                    // Skip to next weekday
                    do {
                        currentDate.setDate(currentDate.getDate() + 1)
                    } while (currentDate.getDay() === 0 || currentDate.getDay() === 6) // Skip Sunday(0) and Saturday(6)
                    break
                case 'weekly':
                    currentDate.setDate(currentDate.getDate() + 7)
                    break
                case 'monthly':
                    currentDate.setMonth(currentDate.getMonth() + 1)
                    break
                default:
                    return dates // Return single occurrence for 'none'
            }
        }

        return dates
    }

    function handleRequired() {
        try {
            let result: any = 'ok'

            // @ts-ignore
            const isEditing = objectId !== 'new'

            // Check if recurrence type is selected (only for new entries)
            if (!isEditing && !recurrenceType) {
                setRecurrenceError('Veuillez sélectionner un type de répétition')
                return
            }

            // Clear recurrence error if type is selected
            setRecurrenceError(undefined)

            // Validate dates based on mode
            if (isEditing || recurrenceType === 'custom') {
                // For editing or custom mode: validate modal form dates
                if (!fields.start_date) {
                    result = {
                        start_date: reduxText[7489], // Required field missing
                    }
                }
            } else if (recurrenceType && recurrenceType !== 'none') {
                // For repetition types: use parent form dates (no validation needed in modal)
                // The dates will come from defaultFields in handleSubmit
            } else {
                // This case shouldn't happen as recurrence type is required
                // But just in case, validate modal dates
                if (!fields.start_date) {
                    result = {
                        start_date: reduxText[7489], // Required field missing
                    }
                }
            }

            if (fields.end_date && fields.start_date) {
                const startDate = new Date(fields.start_date)
                const endDate = new Date(fields.end_date)
                const diffMs = endDate.getTime() - startDate.getTime()
                const diffHours = diffMs / (1000 * 60 * 60)
                if (endDate.getTime() < startDate.getTime()) {
                    result = {
                        end_date: 'La date de fin doit être égale ou postérieure à la date de début.',
                    }
                } else if (diffHours > 24) {
                    result = {
                        end_date: 'La date de fin doit être au maximum 24h après la date de début.',
                    }
                }
                if (endDate < startDate) {
                    result = {
                        end_date: 'La date de fin doit être postérieure à la date de début.',
                    }
                }
            }
            if (result === 'ok') {
                handleSubmit()
            } else {
                setErrors(result)
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'TemplateBlock797MultiDateFormModalSite',
                'handleRequired',
            ))
        }
    }

    function handleSubmit() {
        try {
            const mulidateFields = [
                'description',
                'end_date',
                'start_date',
                'address_json',
                'address_additional',
            ]
            const jsonObject: any = {}
            Object.entries(fields).forEach(([key, value]) => {
                if (
                    mulidateFields.includes(key) &&
                    value !== undefined &&
                    value !== null &&
                    value !== ''
                ) {
                    if (jsonObject.hasOwnProperty(key)) {
                        if (Array.isArray(jsonObject[key])) {
                            jsonObject[key].push(value)
                        } else {
                            jsonObject[key] = [jsonObject[key], value]
                        }
                    } else {
                        jsonObject[key] = value
                    }
                }
            })
            setMultiDateData(prev => {
                // Parse address_json if it's a string
                let addressJsonValue = jsonObject.address_json || defaultFields?.address_json || ''
                if (typeof addressJsonValue === 'string' && addressJsonValue) {
                    try {
                        addressJsonValue = JSON.parse(addressJsonValue)
                    } catch (e) {
                        // If parsing fails, keep it as string
                    }
                }

                // Check if we're editing an existing item
                // @ts-ignore
                if (objectId !== 'new') {
                    // Editing mode - update the existing item
                    const updatedArray = prev.map(item => {
                        if (item.fid === objectId) {
                            return {
                                ...item,
                                description: jsonObject.description || defaultFields?.description || '',
                                address_json: addressJsonValue,
                                address_additional: jsonObject.address_additional || defaultFields?.address_additional || '',
                                start_date: jsonObject.start_date || item.start_date,
                                end_date: jsonObject.end_date || item.end_date,
                            }
                        }
                        return item
                    })
                    onChange(updatedArray)
                    return updatedArray
                }

                // Creating new items
                // Determine which dates to use based on recurrence type
                let startDateToUse: string
                let endDateToUse: string

                if (recurrenceType === 'custom') {
                    // Use dates from the modal form
                    startDateToUse = jsonObject.start_date
                    endDateToUse = jsonObject.end_date
                } else {
                    // Use dates from parent form (defaultFields)
                    // Extract the .value property from the field objects
                    startDateToUse = defaultFields?.start_date?.value || defaultFields?.start_date || jsonObject.start_date
                    endDateToUse = defaultFields?.end_date?.value || defaultFields?.end_date || jsonObject.end_date
                }

                // Validate that we have dates
                if (!startDateToUse) {
                    return prev
                }

                // Generate dates based on recurrence type
                const generatedDates = recurrenceType === 'none' || recurrenceType === 'custom'
                    ? [{ start: new Date(startDateToUse), end: new Date(endDateToUse || startDateToUse) }]
                    : generateRecurrenceDates(
                        startDateToUse,
                        endDateToUse || startDateToUse,
                        recurrenceType!,
                        recurrenceCount
                    )

                // Create items for each generated date
                const newItems = generatedDates.map(dateObj => ({
                    fid: Math.floor(Math.random() * 1000000000),
                    description: jsonObject.description || defaultFields?.description || '',
                    address_json: addressJsonValue,
                    address_additional: jsonObject.address_additional || defaultFields?.address_additional || '',
                    start_date: dateObj.start.toISOString(),
                    end_date: dateObj.end.toISOString(),
                }))

                const newArray = [...prev, ...newItems]
                onChange(newArray)
                return newArray
            })
            onCloseHelperModal()
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'TemplateBlock797MultiDateFormModalSite',
                'handleSubmit',
            ))
        }
    }

    return (
        <Dialog
            className='template-block-797-multi-date-form-modal'
            classes={{ paper: 'mo-dialog-wrapper' }}
            disableEnforceFocus
            maxWidth='xl'
            onClose={() => setDateId(false)}
            open
        >
            <DialogTitle className='mo-dialog-header'>
                <div className='mo-dialog-header-close-wrap'>
                    <IconBlockSite
                        className='mo-dialog-header-close'
                        iconClass='mo-new-icon-times-solid'
                        onClick={onCloseHelperModal}
                    />
                </div>
            </DialogTitle>
            <DialogContent className={`mo-dialog-content mo-hidden-vertical-scrollbar${reduxModalSite.isWindows}`}>
                {/* Block 1: Recurrence - Only show when creating new entries */}
                {isNewEntry && (
                    <div
                        className={`template-block-796 ${deviceType}`}
                        style={{
                            padding: '20px',
                            background: '#f8f9fa',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        <h3
                            style={{
                                color: 'rgb(18, 53, 106)',
                                fontFamily: 'Open Sans',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                marginBottom: '15px',
                                marginTop: '0',
                            }}
                        >
                            Répétition
                        </h3>
                        <div style={{ marginBottom: '15px' }}>
                            <span
                                className={`template-block-779 ${deviceType}`}
                                style={{
                                    color: 'rgb(18, 53, 106)',
                                    fontFamily: 'Open Sans',
                                    fontSize: '14px',
                                    letterSpacing: '0.3px',
                                    lineHeight: 1.5,
                                    padding: '0px 0px 5px',
                                    display: 'block',
                                }}
                            >
                                Type de répétition
                            </span>
                            <select
                                value={recurrenceType || ''}
                                onChange={(e) => {
                                    setRecurrenceType(e.target.value as RecurrenceType)
                                    setRecurrenceError(undefined)
                                }}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    fontSize: '14px',
                                    fontFamily: 'Open Sans',
                                    color: 'rgb(18, 53, 106)',
                                }}
                            >
                                <option value="">Sélectionner...</option>
                                <option value="daily">Tous les jours</option>
                                <option value="weekdays">Tous les jours de la semaine (lun.-ven.)</option>
                                <option value="weekly">Toutes les semaines</option>
                                <option value="monthly">Mensuel</option>
                                <option value="custom">Personnalisé (choisir chaque date)</option>
                            </select>
                            {recurrenceError && (
                                <span
                                    className='t-797-error'
                                    style={{
                                        fontFamily: stylesNew?.fontFamily,
                                    }}
                                >
                                    {recurrenceError}
                                </span>
                            )}
                        </div>
                        {recurrenceType && recurrenceType !== 'none' && recurrenceType !== 'custom' && (
                            <>
                                <div style={{ marginBottom: '15px' }}>
                                    <span
                                        className={`template-block-779 ${deviceType}`}
                                        style={{
                                            color: 'rgb(18, 53, 106)',
                                            fontFamily: 'Open Sans',
                                            fontSize: '14px',
                                            letterSpacing: '0.3px',
                                            lineHeight: 1.5,
                                            padding: '0px 0px 5px',
                                            display: 'block',
                                        }}
                                    >
                                        Nombre de fois (max 10 fois)
                                    </span>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={recurrenceCount}
                                        onChange={(e) => setRecurrenceCount(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '4px',
                                            border: '1px solid #ccc',
                                            fontSize: '14px',
                                            fontFamily: 'Open Sans',
                                            color: 'rgb(18, 53, 106)',
                                        }}
                                    />
                                </div>
                                {fields.start_date && (() => {
                                    // Calculate the last occurrence date
                                    const start = new Date(fields.start_date)
                                    const lastDate = new Date(start)
                                    let occurrencesAdded = 0

                                    while (occurrencesAdded < recurrenceCount - 1) {
                                        switch (recurrenceType) {
                                            case 'daily':
                                                lastDate.setDate(lastDate.getDate() + 1)
                                                break
                                            case 'weekdays':
                                                do {
                                                    lastDate.setDate(lastDate.getDate() + 1)
                                                } while (lastDate.getDay() === 0 || lastDate.getDay() === 6)
                                                break
                                            case 'weekly':
                                                lastDate.setDate(lastDate.getDate() + 7)
                                                break
                                            case 'monthly':
                                                lastDate.setMonth(lastDate.getMonth() + 1)
                                                break
                                            default:
                                                break
                                        }
                                        occurrencesAdded++
                                    }

                                    const labels: Record<string, string> = {
                                        'daily': 'quotidiennes',
                                        'weekdays': 'en semaine',
                                        'weekly': 'hebdomadaires',
                                        'monthly': 'mensuelles',
                                    }

                                    return (
                                        <div style={{ padding: '12px', background: 'rgba(20, 104, 108, 0.1)', borderRadius: '4px', borderLeft: '4px solid rgb(20, 104, 108)', color: 'rgb(18, 53, 106)' }}>
                                            <strong>Aperçu:</strong> {recurrenceCount} occurrence{recurrenceCount > 1 ? 's' : ''} {labels[recurrenceType] || ''} (jusqu'au {lastDate.toLocaleDateString('fr-FR')})
                                        </div>
                                    )
                                })()}
                            </>
                        )}
                    </div>
                )}

                {/* Block 2: Dates - Always show for editing, conditionally for creating with custom */}
                {(!isNewEntry || recurrenceType === 'custom') && (
                    <div
                        className={`template-block-796 ${deviceType}`}
                        style={{
                            padding: '20px',
                            background: '#ffffff',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        <h3
                            style={{
                                color: 'rgb(18, 53, 106)',
                                fontFamily: 'Open Sans',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                marginBottom: '15px',
                                marginTop: '0',
                            }}
                        >
                            Dates et horaires
                        </h3>
                        <span
                            className={`template-block-779 ${deviceType}`}
                            style={{
                                color: 'rgb(18, 53, 106)',
                                fontFamily: 'Open Sans',
                                fontSize: '14px',
                                letterSpacing: '0.3px',
                                lineHeight: 1.5,
                                padding: '0px 0px 5px',
                                display: 'block',
                            }}
                        >
                            Date et heure de début
                        </span>
                        <TemplateBlock797DateTimeSite
                            blockId={'0'}
                            deviceType={deviceType}
                            formInputObj={{
                                data_json: {
                                    min_date_today: true,
                                    time_intervals: 15,
                                },
                                field_type: 'datetime',
                                label: 'Date et heure de début',
                                name: 'start_date',
                            }}
                            format='datetime'
                            handleInputChange={handleInputChange}
                            isEditHovered={false}
                            reduxModalSite={reduxModalSite}
                            stylesNew={stylesNew}
                            tabIndex={0}
                            value={fields.start_date || ''}
                        />
                        {errors.start_date && (
                            <span
                                className='t-797-error'
                                style={{
                                    fontFamily: stylesNew?.fontFamily,
                                }}
                            >
                                {errors.start_date}
                            </span>
                        )}
                        <span
                            className={`template-block-779 ${deviceType}`}
                            style={{
                                color: 'rgb(18, 53, 106)',
                                fontFamily: 'Open Sans',
                                fontSize: '14px',
                                letterSpacing: '0.3px',
                                lineHeight: 1.5,
                                padding: '20px 0px 5px',
                                display: 'block',
                            }}
                        >
                            Date et heure de fin
                        </span>
                        <TemplateBlock797DateTimeSite
                            blockId={'1'}
                            deviceType={deviceType}
                            formInputObj={{
                                data_json: {
                                    min_date_today: true,
                                    time_intervals: 15,
                                },
                                field_type: 'datetime',
                                label: 'Date et heure de fin',
                                name: 'end_date',
                            }}
                            format='datetime'
                            handleInputChange={handleInputChange}
                            isEditHovered={false}
                            reduxModalSite={reduxModalSite}
                            stylesNew={stylesNew}
                            tabIndex={1}
                            value={fields.end_date || ''}
                        />
                        {errors.end_date && (
                            <span
                                className='t-797-error'
                                style={{
                                    fontFamily: stylesNew?.fontFamily,
                                }}
                            >
                                {errors.end_date}
                            </span>
                        )}
                    </div>
                )}

                {/* Block 3: Optional Fields */}
                <div
                    className={`template-block-796 ${deviceType}`}
                    style={{
                        padding: '20px',
                        background: '#ffffff',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e0e0e0',
                    }}
                >
                    <h3
                        style={{
                            color: 'rgb(18, 53, 106)',
                            fontFamily: 'Open Sans',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            marginBottom: '15px',
                            marginTop: '0',
                        }}
                    >
                        Informations complémentaires (facultatif)
                    </h3>
                    <span
                        className={`template-block-779 ${deviceType}`}
                        style={{
                            color: 'rgb(18, 53, 106)',
                            fontFamily: 'Open Sans',
                            fontSize: '14px',
                            letterSpacing: '0.3px',
                            lineHeight: 1.5,
                            padding: '0px 0px 5px',
                            display: 'block',
                        }}
                    >
                        Quelques détails sur l'activité
                    </span>
                    <TemplateBlock797TextAreaSite
                        blockId={'2'}
                        deviceType={deviceType}
                        formInputObj={{
                            label: 'Quelques détails sur l’activité (facultatif)',
                            name: 'description',
                        }}
                        handleInputChange={handleInputChange}
                        isEditHovered={false}
                        reduxModalSite={reduxModalSite}
                        row={1}
                        stylesNew={stylesNew}
                        tabIndex={2}
                        value={fields.description || ''}
                    />
                    {errors.description && (
                        <span
                            className='t-797-error'
                            style={{
                                fontFamily: stylesNew?.fontFamily,
                            }}
                        >
                            {errors.description}
                        </span>
                    )}
                    <span
                        className={`template-block-779 ${deviceType}`}
                        style={{
                            color: 'rgb(18, 53, 106)',
                            fontFamily: 'Open Sans',
                            fontSize: '14px',
                            letterSpacing: '0.3px',
                            lineHeight: 1.5,
                            padding: '20px 0px 5px',
                            display: 'block',
                        }}
                    >
                        Nom et adresse complète du lieu
                    </span>
                    <TemplateBlock797LocationSite
                        blockId={'3'}
                        deviceType={deviceType}
                        formInputObj={{
                            label: 'Nom et adresse complète du lieu.',
                            name: 'address_json',
                        }}
                        handleInputChange={handleInputChange}
                        isEditHovered={false}
                        reduxModalSite={reduxModalSite}
                        stylesNew={stylesNew}
                        tabIndex={3}
                        value={fields.address_json || ''}
                    />
                    {errors.address_json && (
                        <span
                            className='t-797-error'
                            style={{
                                fontFamily: stylesNew?.fontFamily,
                            }}
                        >
                            {errors.address_json}
                        </span>
                    )}
                    <span
                        className={`template-block-779 ${deviceType}`}
                        style={{
                            color: 'rgb(18, 53, 106)',
                            fontFamily: 'Open Sans',
                            fontSize: '14px',
                            letterSpacing: '0.3px',
                            lineHeight: 1.5,
                            padding: '20px 0px 5px',
                            display: 'block',
                        }}
                    >
                        Complément d'adresse
                    </span>
                    <TemplateBlock797TextAreaSite
                        blockId={'4'}
                        deviceType={deviceType}
                        formInputObj={{
                            label: 'Complément d\'adresse(facultatif)',
                            name: 'address_additional',
                        }}
                        handleInputChange={handleInputChange}
                        isEditHovered={false}
                        reduxModalSite={reduxModalSite}
                        row={1}
                        stylesNew={stylesNew}
                        tabIndex={4}
                        value={fields.address_additional || ''}
                    />
                    {errors.address_additional && (
                        <span
                            className='t-797-error'
                            style={{
                                fontFamily: stylesNew?.fontFamily,
                            }}
                        >
                            {errors.address_additional}
                        </span>
                    )}
                </div>
            </DialogContent>
            <DialogActions className='mo-dialog-footer'>
                <div
                    className={`template-block-783 ${deviceType}`}
                    onClick={handleRequired}
                    style={{
                        background: 'rgb(18, 53, 106)',
                        borderColor: 'rgb(18, 53, 106)',
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        color: 'rgb(255, 255, 255)',
                        fontSize: '14px',
                        letterSpacing: '0.3px',
                        padding: '10px',
                        textAlign: 'center',
                        textTransform: 'uppercase',
                        width: '100%',
                        cursor: 'pointer'
                    }}
                >
                    Ajouter
                </div>
            </DialogActions>
        </Dialog>
    )

})
